"use client";

import Image from "next/image";
import { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { z } from "zod";

import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";
import {
  buttonHover,
  ctaClick,
  FireWidgetViewedEvent,
} from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn, formatSectionTitle } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";

const MetadataSchema = z.object({
  id: z.union([z.number(), z.string()]),
  meta_image: z.string().optional().default(""),
  meta_hover_image: z.string().optional().default(""),
  meta_alt_image: z.string().optional().default(""),
  meta_title: z.string().optional().default(""),
  meta_sub_title: z.string().optional().default(""),
  meta_priority: z.coerce.number().optional().default(0),
  /** Per-row on/off from the admin table; absent means on */
  status: z.union([z.boolean(), z.number()]).optional(),
});

const SectionSchema = z
  .object({
    title: z.string().optional().default(""),
    title_start_color: z.string().optional().default("#1F1F1F"),
    title_end_color: z.string().optional().default("#E1473F"),
    description: z.string().optional().default(""),
    description_color: z.string().optional().default("#1F1F1F"),
    /** Lead CTA under the description — empty text hides the button */
    button_text: z.string().optional().default(""),
    button_color: z.string().optional().default("#E1473F"),
    button_text_color: z.string().optional().default("#FFFFFF"),
    /** Desktop hover fill + mobile flip-side fill of the cards */
    card_hover_background_color: z.string().optional().default("#E1473F"),
    desktop_background_color: z.string().optional().default("#FFFFFF"),
    mobile_background_color: z.string().optional().default("#FFFFFF"),
    metadata: z.array(MetadataSchema).optional().default([]),
    status: z.union([z.boolean(), z.number()]).optional(),
  })
  .transform((d) => ({
    ...d,
    metadata: [...(d.metadata || [])]
      .filter((m) => m.status !== false && m.status !== 0)
      .sort((a, b) => (a.meta_priority || 0) - (b.meta_priority || 0)),
  }));

type CardRow = z.infer<typeof MetadataSchema>;

export default function ProgramBenefits1005({ data }: { data: any }) {
  const ref = useRef<HTMLElement>(null);
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const [selectedItem, setSelectedItem] = useState<number | null>(null);
  const [selected, setSelected] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [isCtaHovered, setIsCtaHovered] = useState(false);

  const raw = data?.data ?? data ?? {};
  const parsed = useMemo(() => SectionSchema.safeParse(raw), [raw]);

  useEffect(() => {
    if (ref.current) {
      FireWidgetViewedEvent(ref, "program_benefits_online_experience");
    }
  }, []);

  if (!parsed.success) {
    return null;
  }

  const section = parsed.data;
  const isOff =
    section.status === false ||
    section.status === 0 ||
    String(section.status) === "0";

  if (isOff) {
    return null;
  }

  const rows = section.metadata;
  if (rows.length === 0) {
    return null;
  }

  const hoverBg = section.card_hover_background_color || "#E1473F";
  const accent = section.title_end_color || "#E1473F";
  const ctaText = (section.button_text || "").trim();
  const bgColor = isMobile
    ? section.mobile_background_color
    : section.desktop_background_color;

  const handleCardClick = (index: number) => {
    if (selectedItem === index) {
      setSelected((prev) => !prev);
    } else {
      setSelectedItem(index);
      setSelected(true);
    }
    ctaClick({
      section: "why_cu_best",
      label: "Get Started",
      component: "get_started",
      step: "",
      entity: "",
    });
  };

  return (
    <section
      ref={ref}
      className="flex items-center justify-center [&_*]:![font-family:Poppins,sans-serif]"
      style={
        {
          fontFamily: "Poppins, sans-serif",
          backgroundColor: bgColor,
        } as CSSProperties
      }
      data-widget="program-benefits-1005"
    >
      {/* CU's `w-screen` token is overridden to 1114px — translate it literally, not as 100vw */}
      <div className="w-full pb-[40px] pt-[26px] min-[768px]:px-[50px] min-[768px]:pb-[60px] min-[768px]:pt-[46px] min-[1024px]:w-[1114px]">
        <div className="flex flex-col items-center gap-[34px] min-[768px]:gap-[46px]">
          <div className="mx-auto flex w-[326px] flex-col items-center justify-center gap-5 text-center min-[768px]:w-full">
            <h2
              className="text-center text-[28px] font-semibold leading-[126%] min-[768px]:text-[32px]"
              style={{ color: section.title_start_color || "#1F1F1F" }}
              dangerouslySetInnerHTML={{
                // Inline-style the highlight span: an admin-tab stylesheet
                // loaded before this widget existed won't contain a freshly
                // generated arbitrary utility, so a class can't be trusted here.
                __html: formatSectionTitle(section.title, "").replace(
                  /<span /g,
                  `<span style="color:${accent}" `
                ),
              }}
            />
            {section.description && (
              <p
                className="text-[16px] font-normal leading-[22px]"
                style={{ color: section.description_color || "#1F1F1F" }}
              >
                {section.description}
              </p>
            )}
            {ctaText && (
              <button
                type="button"
                className="flex cursor-pointer items-center justify-center gap-2.5 rounded-md px-[18px] py-3 text-[16px] font-semibold leading-normal"
                style={{
                  backgroundColor: section.button_color || "#E1473F",
                  color: section.button_text_color || "#FFFFFF",
                }}
                onClick={() =>
                  dispatch(
                    handleOpenLeaddrop({
                      variant: LEAD_DROP_VARIANTS.REGISTER,
                    })
                  )
                }
                onMouseEnter={() => setIsCtaHovered(true)}
                onMouseLeave={() => setIsCtaHovered(false)}
              >
                {ctaText}
                <span className="size-6">
                  <ArrowUpRight
                    className={cn(
                      "transition-transform duration-300",
                      isCtaHovered ? "rotate-[45deg]" : "rotate-0"
                    )}
                  />
                </span>
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4 min-[768px]:gap-5">
            {rows.map((row: CardRow, index: number) => {
              const isFlipped = Boolean(isMobile) && selected && selectedItem === index;
              const isHovered = !isMobile && hoveredIndex === index;
              const displayIcon =
                isHovered && row.meta_hover_image
                  ? row.meta_hover_image
                  : row.meta_image;

              return (
                <div
                  key={String(row.id)}
                  onClick={() => handleCardClick(index)}
                  className="relative h-[212px] w-[168px] [perspective:1000px] min-[768px]:h-[268px] min-[768px]:w-[264px]"
                >
                  <div
                    className="relative h-full w-full transition-transform duration-500"
                    style={{
                      transformStyle: "preserve-3d",
                      WebkitTransformStyle: "preserve-3d",
                      transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
                      WebkitTransform: isFlipped
                        ? "rotateY(180deg)"
                        : "rotateY(0deg)",
                    }}
                  >
                    {/* Front */}
                    <div
                      className="absolute h-full w-full"
                      style={{
                        WebkitBackfaceVisibility: "hidden",
                        backfaceVisibility: "hidden",
                      }}
                    >
                      <div
                        className={cn(
                          "relative flex h-full w-full flex-col items-start justify-start gap-[14px] rounded-xl",
                          "px-[14px] pb-[18px] pt-[14px] shadow-[0px_4px_7.2px_0px_#0000002E]",
                          "transition-colors duration-300 min-[768px]:p-6"
                        )}
                        style={{ backgroundColor: isHovered ? hoverBg : "#FFFFFF" }}
                      >
                        {displayIcon && (
                          <div className="relative size-[42px] shrink-0 rounded-lg">
                            <Image
                              src={displayIcon}
                              alt={row.meta_alt_image || row.meta_title || ""}
                              fill
                              className="rounded-lg object-cover"
                              sizes="42px"
                            />
                          </div>
                        )}
                        <div className="flex h-full flex-col gap-0 min-[768px]:gap-[14px]">
                          {row.meta_title && (
                            <h3
                              className="text-left text-[16px] font-semibold leading-normal min-[768px]:leading-[1.5]"
                              style={{ color: isHovered ? "#FFFFFF" : "#171719" }}
                            >
                              {row.meta_title}
                            </h3>
                          )}
                          {row.meta_sub_title && (
                            <p className="hidden text-left text-[14px] font-normal leading-[1.5] min-[768px]:block" style={{ color: isHovered ? "#FFFFFF" : "#171719" }}>
                              {row.meta_sub_title}
                            </p>
                          )}
                          <div
                            className="absolute inset-0"
                            onMouseEnter={() => {
                              setHoveredIndex(index);
                              if (!isMobile) {
                                buttonHover({
                                  section: "online_program",
                                  label: row.meta_title || "",
                                });
                              }
                            }}
                            onMouseLeave={() => setHoveredIndex(null)}
                          />
                          <div
                            className="mt-auto flex items-center gap-1 min-[768px]:hidden"
                            style={{ color: accent }}
                          >
                            <p className="text-[14px] font-semibold leading-5">
                              View More
                            </p>
                            <ArrowRight className="size-6" />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Back (reached by tap-to-flip on mobile only) */}
                    <div
                      className="absolute h-full w-full"
                      style={{
                        WebkitBackfaceVisibility: "hidden",
                        backfaceVisibility: "hidden",
                        transform: "rotateY(180deg)",
                        WebkitTransform: "rotateY(180deg)",
                      }}
                    >
                      <div
                        className="flex h-full w-full flex-col justify-between rounded-xl pb-[18px] pl-[14px] pr-4 pt-[14px] text-[#FFFFFF] shadow-[0px_4px_7.2px_0px_#0000002E]"
                        style={{ backgroundColor: hoverBg }}
                      >
                        {row.meta_sub_title && (
                          <p className="text-left text-[14px] font-normal leading-[20px]">
                            {row.meta_sub_title}
                          </p>
                        )}
                        <div className="mt-auto flex items-center gap-1 text-[#FFFFFF] min-[768px]:hidden">
                          <p className="text-[14px] font-semibold leading-5">
                            View less
                          </p>
                          <ArrowRight className="size-6" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
